import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import StatsCards from "@/pages/admin/dashboard/components/StatsCards";
import type { StatItem } from "@/pages/admin/dashboard/components/StatsCards";
import AnnouncementForm from "@/pages/seller/dashboard/components/AnnouncementForm";
import AnnouncementList from "@/pages/seller/dashboard/components/AnnouncementList";
import SettingsTab from "@/pages/seller/dashboard/components/SettingsTab";
import TableSkeleton from "@/pages/admin/dashboard/components/TableSkeleton";
import type { SellerProduct } from "@/pages/seller/dashboard/types";

type TabKey = "create" | "list" | "settings";

interface TabDef {
  key: TabKey;
  label: string;
  icon: string;
}

const TABS: TabDef[] = [
  { key: "create", label: "Crear anuncio", icon: "ri-add-line" },
  { key: "list", label: "Mis anuncios", icon: "ri-store-2-line" },
  { key: "settings", label: "Ajustes", icon: "ri-settings-3-line" },
];

export default function SellerDashboard() {
  const { profile, user, sellerStatus, role } = useAuth();
  const isAdmin = role === "admin" || role === "super_admin";
  const [activeTab, setActiveTab] = useState<TabKey>("create");
  const [products, setProducts] = useState<SellerProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SellerProduct | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from("products")
        .select("*")
        .order("created_at", { ascending: false });
      if (err) throw err;
      setProducts((data ?? []) as SellerProduct[]);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "No se pudieron cargar tus anuncios."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const draftCount = useMemo(
    () => products.filter((p) => p.status === "draft").length,
    [products]
  );

  const stats: StatItem[] = useMemo(() => {
    const active = products.filter((p) => p.status === "active").length;
    const pending = products.filter((p) =>
      ["pending_moderation", "pending_completion", "rejected"].includes(p.status)
    ).length;
    const sold = products.filter((p) => p.status === "sold").length;
    return [
      {
        label: "Anuncios totales",
        value: String(products.length),
        icon: "ri-store-2-line",
        tone: "primary",
      },
      {
        label: "Disponibles",
        value: String(active),
        icon: "ri-checkbox-circle-line",
        tone: "secondary",
      },
      {
        label: "Pendientes",
        value: String(pending),
        icon: "ri-time-line",
        tone: "accent",
      },
      {
        label: "Vendidos",
        value: String(sold),
        icon: "ri-hand-coin-line",
        tone: "neutral",
      },
    ];
  }, [products]);

  const handleEdit = (product: SellerProduct) => {
    setEditing(product);
    setActiveTab("create");
  };

  const handleFormSuccess = () => {
    setEditing(null);
    fetchProducts();
    setActiveTab("list");
  };

  const handleCancelEdit = () => {
    setEditing(null);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <main className="mx-auto px-4 py-8 md:px-6 lg:max-w-7xl">
        {/* Encabezado */}
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
            <i className="ri-user-line text-xl text-primary-500"></i>
          </span>
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">
              Hola, {profile?.full_name ?? "Vendedor"}
            </h1>
            <p className="text-sm text-foreground-600">{profile?.email}</p>
          </div>
          <span className="ml-auto whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-700">
            {isAdmin
              ? "Publicación directa"
              : sellerStatus === "approved"
                ? "Vendedor verificado"
                : sellerStatus === "pending" || sellerStatus === "none"
                  ? "Vendedor en revisión"
                  : sellerStatus === "suspended"
                    ? "Vendedor suspendido"
                    : "Vendedor"}
          </span>
        </div>

        {/* Banner de borradores pendientes */}
        {draftCount > 0 && activeTab !== "list" && (
          <div className="mb-6 flex items-center gap-3 rounded-lg border border-accent-200 bg-accent-100/60 px-4 py-3">
            <i className="ri-draft-line text-accent-700"></i>
            <p className="text-sm font-medium text-accent-800">
              Tienes {draftCount} anuncio{draftCount === 1 ? "" : "s"} pendiente
              {draftCount === 1 ? "" : "s"} de completar.
            </p>
            <button
              type="button"
              onClick={() => setActiveTab("list")}
              className="ml-auto cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-accent-600"
            >
              Ver borradores
            </button>
          </div>
        )}

        {/* Métricas */}
        <StatsCards stats={stats} />

        {/* Pestañas */}
        <div className="mt-8">
          <div className="inline-flex items-center gap-1 rounded-full border border-background-200/70 bg-background-100 p-1">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  activeTab === tab.key
                    ? "bg-primary-500 text-white"
                    : "text-foreground-500 hover:text-foreground-900"
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                {tab.label}
                {tab.key === "list" && (
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                      activeTab === tab.key
                        ? "bg-white/20 text-white"
                        : "bg-background-200 text-foreground-600"
                    }`}
                  >
                    {products.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Contenido */}
          <div className="mt-4 overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
            {activeTab === "settings" ? (
              <SettingsTab
                userId={user?.id ?? ""}
                initialWhatsapp={profile?.whatsapp ?? ""}
              />
            ) : activeTab === "create" ? (
              <AnnouncementForm
                key={editing?.id ?? "create"}
                editing={editing}
                userId={user?.id ?? ""}
                onSuccess={handleFormSuccess}
                onCancelEdit={handleCancelEdit}
              />
            ) : loading ? (
              <TableSkeleton rows={4} />
            ) : error ? (
              <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-error-warning-line text-2xl text-accent-700"></i>
                </span>
                <p className="mt-4 text-sm font-semibold text-foreground-900">
                  Ocurrió un error
                </p>
                <p className="mt-1 max-w-sm text-sm text-foreground-500">
                  {error}
                </p>
                <button
                  type="button"
                  onClick={fetchProducts}
                  className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
                >
                  Reintentar
                </button>
              </div>
            ) : (
              <AnnouncementList
                products={products}
                isAdmin={isAdmin}
                onEdit={handleEdit}
                onRefresh={fetchProducts}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}